import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Upload, X } from "lucide-react";
import { supabase, uploadImage, generateSlug } from "../../lib/supabase";
import { Spinner } from "../../components/common/UI";
import toast from "react-hot-toast";

const CUISINE_OPTIONS = ["Biryani", "BBQ", "Karahi", "Desi", "Fast Food", "Sweets", "Drinks", "Burgers", "Pizza", "Seafood"];
const CITIES = ["Burewala", "Vehari"];

const defaultForm = {
  name: "", slug: "", description: "", location: "", city: "Burewala",
  address: "", contact: "", whatsapp: "", email: "", cuisine_type: [],
  opening_time: "09:00", closing_time: "23:00", is_open: true,
  is_featured: false, is_active: true, min_order: 0, delivery_fee: 0,
  owner_id: "",
};

export default function AdminRestaurantEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState(defaultForm);
  const [owners, setOwners] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      // Load restaurant owners
      const { data: users } = await supabase
        .from("users")
        .select("id, name, email")
        .eq("role", "restaurant");
      setOwners(users || []);

      if (isEdit) {
        const { data } = await supabase.from("restaurants").select("*").eq("id", id).single();
        if (data) {
          setForm({ ...defaultForm, ...data });
          setLogoPreview(data.logo_url);
          setCoverPreview(data.cover_url);
        }
        setLoading(false);
      }
    };
    loadData();
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    if (name === "name" && !isEdit) {
      setForm((f) => ({ ...f, name: value, slug: generateSlug(value) }));
    }
  };

  const toggleCuisine = (c) => {
    setForm((f) => ({
      ...f,
      cuisine_type: f.cuisine_type.includes(c)
        ? f.cuisine_type.filter((x) => x !== c)
        : [...f.cuisine_type, c],
    }));
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    if (type === "logo") { setLogoFile(file); setLogoPreview(preview); }
    else { setCoverFile(file); setCoverPreview(preview); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.contact || !form.location) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSaving(true);
    try {
      let logo_url = form.logo_url;
      let cover_url = form.cover_url;

      if (logoFile) {
        logo_url = await uploadImage("restaurant-images", logoFile, `logos/${Date.now()}-${logoFile.name}`);
      }
      if (coverFile) {
        cover_url = await uploadImage("restaurant-images", coverFile, `covers/${Date.now()}-${coverFile.name}`);
      }

      const payload = {
        ...form,
        logo_url,
        cover_url,
        slug: form.slug || generateSlug(form.name),
        owner_id: form.owner_id || null,
        min_order: parseFloat(form.min_order) || 0,
        delivery_fee: parseFloat(form.delivery_fee) || 0,
      };

      if (isEdit) {
        const { error } = await supabase.from("restaurants").update(payload).eq("id", id);
        if (error) throw error;
        toast.success("Restaurant updated!");
      } else {
        const { error } = await supabase.from("restaurants").insert(payload);
        if (error) throw error;
        toast.success("Restaurant created!");
      }
      navigate("/admin/restaurants");
    } catch (err) {
      toast.error(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Spinner size="lg" /></div>;

  return (
    <div className="max-w-3xl page-enter">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin/restaurants" className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </Link>
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">
            {isEdit ? "Edit Restaurant" : "Add Restaurant"}
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Fill in the restaurant details below</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Images */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
          <h2 className="font-semibold text-gray-900">📸 Images</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Cover */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">Cover Image</label>
              <div className="relative h-36 bg-gray-100 rounded-xl overflow-hidden border-2 border-dashed border-gray-300 hover:border-orange-400 transition-colors">
                {coverPreview ? (
                  <>
                    <img src={coverPreview} alt="Cover" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => { setCoverPreview(null); setCoverFile(null); }} className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <label className="flex flex-col items-center justify-center h-full cursor-pointer text-gray-400 hover:text-orange-500 transition-colors">
                    <Upload className="w-6 h-6 mb-1" />
                    <span className="text-xs">Upload cover</span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileChange(e, "cover")} />
                  </label>
                )}
              </div>
            </div>
            {/* Logo */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">Logo</label>
              <div className="relative h-36 bg-gray-100 rounded-xl overflow-hidden border-2 border-dashed border-gray-300 hover:border-orange-400 transition-colors">
                {logoPreview ? (
                  <>
                    <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => { setLogoPreview(null); setLogoFile(null); }} className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <label className="flex flex-col items-center justify-center h-full cursor-pointer text-gray-400 hover:text-orange-500 transition-colors">
                    <Upload className="w-6 h-6 mb-1" />
                    <span className="text-xs">Upload logo</span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileChange(e, "logo")} />
                  </label>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
          <h2 className="font-semibold text-gray-900">📋 Basic Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Name *</label>
              <input name="name" value={form.name} onChange={handleChange} required placeholder="Biryani House" className="input-field" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">URL Slug</label>
              <input name="slug" value={form.slug} onChange={handleChange} placeholder="biryani-house" className="input-field font-mono text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Description</label>
            <textarea name="description" value={form.description} onChange={handleChange} rows={3} placeholder="Describe the restaurant..." className="input-field resize-none" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">City *</label>
              <select name="city" value={form.city} onChange={handleChange} className="input-field">
                {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Location / Area *</label>
              <input name="location" value={form.location} onChange={handleChange} required placeholder="Main Bazar, Burewala" className="input-field" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Full Address</label>
            <input name="address" value={form.address} onChange={handleChange} placeholder="Street 5, Near Jamia Mosque..." className="input-field" />
          </div>
        </div>

        {/* Contact */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
          <h2 className="font-semibold text-gray-900">📞 Contact</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Phone *</label>
              <input name="contact" value={form.contact} onChange={handleChange} required placeholder="03XX-XXXXXXX" className="input-field" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">WhatsApp</label>
              <input name="whatsapp" value={form.whatsapp} onChange={handleChange} placeholder="923XXXXXXXXX" className="input-field" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Email</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="restaurant@email.com" className="input-field" />
            </div>
          </div>
        </div>

        {/* Cuisine & Hours */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
          <h2 className="font-semibold text-gray-900">🍛 Cuisine & Hours</h2>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">Cuisine Types</label>
            <div className="flex flex-wrap gap-2">
              {CUISINE_OPTIONS.map((c) => (
                <button key={c} type="button" onClick={() => toggleCuisine(c)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.cuisine_type.includes(c) ? "bg-orange-500 text-white border-orange-500" : "border-gray-200 text-gray-600 hover:border-orange-300"}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Opens At</label>
              <input type="time" name="opening_time" value={form.opening_time} onChange={handleChange} className="input-field" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Closes At</label>
              <input type="time" name="closing_time" value={form.closing_time} onChange={handleChange} className="input-field" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Min Order (₨)</label>
              <input type="number" name="min_order" value={form.min_order} onChange={handleChange} min="0" className="input-field" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Delivery Fee (₨)</label>
              <input type="number" name="delivery_fee" value={form.delivery_fee} onChange={handleChange} min="0" className="input-field" />
            </div>
          </div>
        </div>

        {/* Owner & Settings */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
          <h2 className="font-semibold text-gray-900">⚙️ Settings</h2>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Restaurant Owner</label>
            <select name="owner_id" value={form.owner_id || ""} onChange={handleChange} className="input-field">
              <option value="">— No owner assigned —</option>
              {owners.map((o) => (
                <option key={o.id} value={o.id}>{o.name} ({o.email})</option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap gap-6">
            {[
              { name: "is_active", label: "Active (visible to public)" },
              { name: "is_open", label: "Currently Open" },
              { name: "is_featured", label: "Featured on Homepage" },
            ].map(({ name, label }) => (
              <label key={name} className="flex items-center gap-2.5 cursor-pointer">
                <div
                  onClick={() => setForm((f) => ({ ...f, [name]: !f[name] }))}
                  className={`w-10 h-6 rounded-full transition-colors flex items-center px-1 ${form[name] ? "bg-orange-500" : "bg-gray-300"}`}
                >
                  <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${form[name] ? "translate-x-4" : "translate-x-0"}`} />
                </div>
                <span className="text-sm font-medium text-gray-700">{label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex gap-3 pb-8">
          <Link to="/admin/restaurants" className="btn-secondary flex-1 text-center">Cancel</Link>
          <button type="submit" disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2">
            {saving ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</> : isEdit ? "Update Restaurant" : "Create Restaurant"}
          </button>
        </div>
      </form>
    </div>
  );
}
